import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, MessageSquare, CheckCircle } from 'lucide-react';
import { AppointmentResponse, appointmentApiService } from '../../services/appointmentApi';

interface AppointmentData {
  subject: string;
  message: string;
  date: string;
  time: string;
  appointmentType: string;
}

interface ConfirmationViewProps {
  appointmentData: AppointmentData;
  createdAppointment: AppointmentResponse | null;
  onBack: () => void;
}

const ConfirmationView: React.FC<ConfirmationViewProps> = ({
  appointmentData,
  createdAppointment,
  onBack
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-ice flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl p-8 shadow-xl max-w-lg w-full"
      >
        {/* Header avec logo */}
        <div className="text-center mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-elegant text-white-gold mb-2">
            Iris Softech
          </h1>
          <p className="text-sm text-gray-600">Confirmation de rendez-vous</p>
        </div>

        {/* Avatar Iris avec message de succès */}
        <div className="flex items-start space-x-4 mb-8">
          <div className="flex-shrink-0">
            <div className="relative">
              <div className="w-16 h-16 rounded-full overflow-hidden border-3 border-green-200 shadow-lg">
                <img 
                  src="/irisavatar.jpg" 
                  alt="Iris IA" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                <CheckCircle className="w-4 h-4 text-white" />
              </div>
            </div>
          </div>
          
          <div className="flex-1">
            <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-4 relative">
              <div className="absolute left-0 top-4 transform -translate-x-2">
                <div className="w-4 h-4 bg-gradient-to-r from-green-50 to-blue-50 rotate-45 border-l border-b border-green-100"></div>
              </div>
              
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                🎉 Rendez-vous confirmé !
              </h3>
              <p className="text-gray-700 leading-relaxed">
                {createdAppointment ? 
                  appointmentApiService.generateConfirmationMessage(createdAppointment.appointment) :
                  `Parfait ! Votre rendez-vous est programmé pour le ${formatDate(appointmentData.date)} à ${appointmentData.time}. Je vous enverrai un rappel par email 24h avant notre rencontre.`
                }
              </p>
            </div>
          </div>
        </div>

        {/* Détails du rendez-vous */}
        <div className="bg-gray-50 rounded-xl p-6 mb-6">
          <h4 className="font-semibold text-gray-900 mb-4">📋 Détails de votre rendez-vous :</h4>
          <div className="space-y-3">
            {createdAppointment && (
              <div className="flex items-center space-x-3">
                <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center">
                  <span className="text-purple-600 text-xs">#</span>
                </div>
                <span className="text-sm text-gray-700">ID: {createdAppointment.appointment.id}</span>
              </div>
            )}
            <div className="flex items-center space-x-3">
              <Calendar className="w-5 h-5 text-blue-600" />
              <span className="text-sm text-gray-700">
                {createdAppointment ? 
                  appointmentApiService.formatDate(createdAppointment.appointment.date) : 
                  formatDate(appointmentData.date)
                }
              </span>
            </div>
            <div className="flex items-center space-x-3">
              <Clock className="w-5 h-5 text-blue-600" />
              <span className="text-sm text-gray-700">
                {createdAppointment ? 
                  `${appointmentApiService.formatTime(createdAppointment.appointment.start_time)} - ${appointmentApiService.formatTime(createdAppointment.appointment.end_time)}` : 
                  appointmentData.time
                }
              </span>
            </div>
            <div className="flex items-center space-x-3">
              <MessageSquare className="w-5 h-5 text-blue-600" />
              <span className="text-sm text-gray-700">
                {createdAppointment ? createdAppointment.appointment.subject : appointmentData.subject}
              </span>
            </div>
            <div className="flex items-center space-x-3">
              {(createdAppointment ? createdAppointment.appointment.appointment_type : appointmentData.appointmentType) === 'online' ? (
                <>
                  <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-600 text-xs">💻</span>
                  </div>
                  <span className="text-sm text-gray-700">Rendez-vous en ligne</span>
                </>
              ) : (
                <>
                  <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 text-xs">🏢</span>
                  </div>
                  <span className="text-sm text-gray-700">Rendez-vous physique</span>
                </>
              )}
            </div>
          </div>
        </div>

        <button
          onClick={onBack}
          className="btn-primary w-full"
        >
          Retour à l'accueil
        </button>
      </motion.div>
    </div>
  );
};

export default ConfirmationView;
